import React from 'react';
import { Container, Typography, Grid, Box, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import RouterIcon from '@mui/icons-material/Router';
import PaymentIcon from '@mui/icons-material/Payment';
import WifiIcon from '@mui/icons-material/Wifi';

const Requirements = () => {
  return (
    <Box
      component="section"
      id="requirements"
      sx={{
        py: { xs: 10, md: 18 },
        backgroundColor: '#ffffff',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.03em',
              background: 'linear-gradient(135deg, #19b594 0%, #0b74c1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 2,
            }}
          >
            Requirements
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              color: 'rgba(29,29,31,0.65)',
              fontSize: 'clamp(1.05rem, 0.3vw + 0.5rem, 1.25rem)',
              maxWidth: '600px',
              mx: 'auto',
              mb: { xs: 6, md: 10 },
              lineHeight: 1.6,
            }}
          >
            Everything you need to get started with NasRadius.
          </Typography>
        </motion.div>

        <Grid container spacing={{ xs: 3, md: 6 }}>
          {/* Hardware Requirements */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              style={{ height: '100%' }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  height: '100%',
                  borderRadius: 4,
                  border: '1px solid rgba(0,0,0,0.06)',
                  backgroundColor: '#fafafa',
                  transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 16px 40px rgba(0,0,0,0.08)',
                    borderColor: 'rgba(11,116,193,0.15)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <RouterIcon sx={{ fontSize: { xs: '1.75rem', md: '2rem' }, color: '#0b74c1', mr: 1.5 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                    Hardware Requirements
                  </Typography>
                </Box>

                {[
                  {
                    icon: <RouterIcon sx={{ color: '#0b74c1', fontSize: '1.5rem', flexShrink: 0, mt: 0.25 }} />,
                    title: 'MikroTik Router',
                    desc: 'RouterOS version <strong>7.0 or higher</strong> is required for full compatibility with NasRadius features.',
                  },
                  {
                    icon: <WifiIcon sx={{ color: '#0b74c1', fontSize: '1.5rem', flexShrink: 0, mt: 0.25 }} />,
                    title: 'Internet Connection',
                    desc: 'A stable internet connection is required on the MikroTik router for remote management, configuration updates, and communication with NasRadius services.',
                  },
                ].map((item, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      p: 2.5,
                      backgroundColor: '#ffffff',
                      borderRadius: 3,
                      border: '1px solid rgba(0,0,0,0.04)',
                      mt: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#f5faff',
                        borderColor: 'rgba(11,116,193,0.1)',
                      },
                    }}
                  >
                    {item.icon}
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#1d1d1f', mb: 0.5, fontSize: { xs: '1rem', md: '1.1rem' } }}>
                        {item.title}
                      </Typography>
                      <Typography sx={{ color: 'rgba(29,29,31,0.65)', fontSize: { xs: '0.9rem', md: '0.95rem' }, lineHeight: 1.6 }}>
                        <span dangerouslySetInnerHTML={{ __html: item.desc }} />
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Paper>
            </motion.div>
          </Grid>

          {/* Payment Gateway Requirements */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              style={{ height: '100%' }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  height: '100%',
                  borderRadius: 4,
                  border: '1px solid rgba(0,0,0,0.06)',
                  backgroundColor: '#fafafa',
                  transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 16px 40px rgba(0,0,0,0.08)',
                    borderColor: 'rgba(11,116,193,0.15)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <PaymentIcon sx={{ fontSize: { xs: '1.75rem', md: '2rem' }, color: '#0b74c1', mr: 1.5 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                    Payment Gateway
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    color: 'rgba(29,29,31,0.6)',
                    mb: 3,
                    fontSize: { xs: '0.9rem', md: '0.95rem' },
                    fontStyle: 'italic',
                  }}
                >
                  Choose one of the following payment gateway options:
                </Typography>

                {[
                  {
                    title: 'Option 1: NasRadiusPay',
                    bg: '#eef8f7',
                    border: '#c8e9e5',
                    items: ['Works with Till, PayBill, Bank PayBill', 'No personal M-PESA keys required'],
                    desc: 'For users without M-PESA credentials who want to receive payments to any Till, PayBill, or Bank PayBill. NasRadiusPay handles setup and routing for you.',
                  },
                  {
                    title: 'Option 2: M-PESA',
                    bg: '#f8f9fa',
                    border: '#d6dde3',
                    items: ['Paybill/Till number required', 'Consumer Key + Secret required'],
                    desc: null,
                  },
                  {
                    title: 'Option 3: KopoKopo',
                    bg: '#f8f9fa',
                    border: '#d6dde3',
                    items: ['Client ID + Secret required', 'STK Till number required'],
                    desc: null,
                  },
                ].map((option, i) => (
                  <Box
                    key={i}
                    sx={{
                      p: 2.5,
                      backgroundColor: option.bg,
                      borderRadius: 3,
                      border: `1px solid ${option.border}`,
                      mb: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.06)',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: '#00796b',
                        mb: option.desc ? 1.5 : 1,
                        fontSize: { xs: '0.95rem', md: '1.05rem' },
                      }}
                    >
                      {option.title}
                    </Typography>
                    {option.desc && (
                      <Typography
                        sx={{
                          color: '#37474f',
                          mb: 1.5,
                          fontSize: { xs: '0.85rem', md: '0.9rem' },
                          lineHeight: 1.6,
                        }}
                      >
                        {option.desc}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 1 }}>
                      {option.items.map((item, j) => (
                        <Typography key={j} sx={{ color: '#37474f', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          • {item}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Requirements;
